import { ShieldCheck, MailMinus, BellRing, Quote } from "lucide-react";

/**
 * SidebarInfo — a sticky reassurance panel that sits beside the form on
 * desktop. Renders the brand mission + 3 calm facts + a real participant
 * quote so users feel held while filling out sensitive details.
 *
 * Props:
 *   audience: "patient" | "caregiver" — adjusts the quote attribution.
 */
const FACTS = [
  {
    icon: ShieldCheck,
    title: "Who sees this?",
    text: "Your data is encrypted at rest and only shared with verified researchers after your explicit consent.",
  },
  {
    icon: BellRing,
    title: "How often we contact you",
    text: "Only when a study matches your profile. No newsletters, no spam — usually a handful of times a year.",
  },
  {
    icon: MailMinus,
    title: "You're in control",
    text: "Withdraw at any time with one click. We'll delete your record and stop contact within 48 hours.",
  },
];

export const SidebarInfo = ({ audience = "patient" }) => {
  const quote =
    audience === "caregiver"
      ? {
          text:
            "I've spent years explaining what my mum needs. This was the first time anyone wrote it down to actually do something with it.",
          name: "Sara",
          role: "Caregiver, UK",
        }
      : {
          text:
            "I've explained my condition a hundred times. This was the first time anyone really wanted to know.",
          name: "Oliver",
          role: "Patient, USA",
        };

  return (
    <aside className="lg:sticky lg:top-28 flex flex-col gap-6">
      {/* Reassurance card */}
      <div className="rounded-3xl bg-[var(--dark-bg)] text-white p-7 md:p-8 relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-[var(--lime)]/10 blur-3xl"
        />
        <span className="relative inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-4">
          <span className="w-6 h-px bg-[var(--lime)]" />
          Why we ask
        </span>
        <h3 className="relative font-['Outfit'] text-xl font-bold leading-tight mb-5">
          Your details, looked after.
        </h3>
        <ul className="relative space-y-5">
          {FACTS.map(({ icon: Icon, title, text }) => (
            <li key={title} className="flex gap-3">
              <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--lime)]/10 flex-shrink-0">
                <Icon size={16} className="text-[var(--lime)]" strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white mb-0.5">
                  {title}
                </p>
                <p className="text-[13px] text-white/80 leading-relaxed">{text}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Quote card */}
      <div className="rounded-3xl bg-white border border-[var(--light-border)] p-6 md:p-7">
        <Quote
          size={22}
          className="text-[var(--lime)] mb-3"
          strokeWidth={1.5}
        />
        <p className="text-[15px] font-['Outfit'] font-semibold text-[var(--charcoal)] leading-snug mb-4">
          “{quote.text}”
        </p>
        <p className="text-xs text-[var(--text-muted)]">
          <span className="font-semibold text-[var(--charcoal)]">
            {quote.name}
          </span>{" "}
          · {quote.role}
        </p>
      </div>
    </aside>
  );
};

export default SidebarInfo;
